/**
 * Vector.
 * 
 * @author Stephen Paul Hassall
 * @license https://CodeRunDebug.com/license.html
 * @web https://CodeRunDebug.com
 */
export default class Vector {
    /**
     * Create vector with values set.
     * @param {Number} x The starting X value.
     * @param {Number} y The starting Y value.
     */
    constructor(x, y) {
        // Set values
        this._x = x;
        this._y = y;
    }

    /**
     * Get the X value.
     * @return {Number} The X amount.
     */
    get x() {
        // Return the  X value
        return this._x;
    }

    /**
     * Set the X value.
     * @param {Number} The X amount to set.
     */
    set x(value) {
        // Set the  X value
        this._x = value;
    }

    /**
     * Get the Y value.
     * @return {Number} The Y amount.
     */
    get y() {
        // Return the  Y value
        return this._y;
    }

    /**
     * Set the Y value.
     * @param {Number} The Y amount to set.
     */
    set y(value) {
        // Set the  Y value
        this._y = value;
    }

    /**
     * Clone the vector into a new vector with the same values.
     * @return {Vector} A new vector with the same values.
     */
    clone() {
        // Create vector object
        const vector = new Vector();

        // Set values
        vector.x = this._x;
        vector.y = this._y;

        // Return the new vector object
        return vector;
    }

    /**
     * Rotate the vector around its origin point.
     * @param {Number} radians The number of radians to rotate the vector point.
     */
    rotate(radians) {
        // Workout new X/Y points
        const newX = (this._x * Math.cos(radians)) - (this._y * Math.sin(radians));
        const newY = (this._x * Math.sin(radians)) + (this._y * Math.cos(radians));

        // Set the new X/Y points
        this._x = newX;
        this._y = newY;
    };

    /**
     * Scale the vector size by the given scalar amount (from the origin point).
     * @param {Number} scalar The amount to scale the vector by.
     */
    scale(scalar) {
        // Scale both the X/Y parts
        this._x *= scalar;
        this._y *= scalar;
    }

    /**
     * Translate (move) the vector by the given dx/dy parts.
     * @param {Number} dx The amount to translate the vector by along the X axis.
     * @param {Number} dy The amount to translate the vector by along the Y axis.
     */
    translate(dx, dy) {
        // Translate the X/Y parts
        this._x += dx;
        this._y += dy;
    }
}
